import type { CSSProperties } from 'react';
import type { Breakpoint } from '../types';

/**
 * Handle converting numbers to spacing matching MUI.
 *
 * @param size A number to convert to spacing.
 * @returns Converted spacing.
 */
export function calcSpacing(size: number) {
  return `${size * 8}px`;
}

/**
 * Handle converting direction to flex direction.
 *
 * @param direction The direction to convert.
 * @returns Converted flex direction.
 */
export function getFlexDirectionClassname(
  direction?: 'row' | 'row-reverse' | 'column' | 'column-reverse'
) {
  switch (direction) {
    case 'row':
      return 'flex-row';
    case 'row-reverse':
      return 'flex-row-reverse';
    case 'column-reverse':
      return 'flex-col-reverse';
    default:
      return 'flex-col';
  }
}

/**
 * Check if a value is undefined or null.
 *
 * @param value The value to check.
 * @returns If the value is undefined or null.
 */
// biome-ignore lint/suspicious/noExplicitAny: <explanation>
export function isNil(value: any): value is undefined | null {
  return value === undefined || value === null;
}

/**
 * Get the responsive value for a breakpoint.
 *
 * @param value A number or a record of breakpoints.
 * @param breakpoint  The breakpoint to get the value for.
 * @returns A number or null.
 */
export function getResponsiveValue<V extends string | number>(
  value: V | Partial<Record<Breakpoint, V>> | null,
  breakpoint: Breakpoint
) {
  if (isNil(value)) {
    return null;
  }

  if (typeof value === 'number' || typeof value === 'string') {
    return null;
  }

  return value[breakpoint] ?? null;
}

/**
 * Generate a CSSProperties object from a record of CSS variables, and remove any undefined or null values.
 *
 * @param vars A record of CSS variables.
 * @returns A CSSProperties object.
 */
export function styleVars<V extends Record<string, string | number | undefined | null>>(
  vars: V,
  transform?: (value: string | number, key: string) => string
): CSSProperties {
  const finalVars = {} as Record<string, string>;

  for (const key in vars) {
    if (vars[key] !== undefined && vars[key] !== null) {
      const v = transform ? transform(vars[key], key) : `${vars[key]}`;
      finalVars[key] = `${v}`;
    }
  }

  return {
    ...finalVars,
  } as CSSProperties;
}
